import io from 'socket.io-client';
import { API, COLOR_VAR, EMPTY_FN, HOST } from '~/constants';
import { Message } from '@fx-ui/fine-design';
import { assign, getApi, getBasePathRouter, getUUID, includes, openInNewTab, post } from '~/utils';
import Intl from '@fx-ui/intl';
import React from 'react';
import IconFont from '~/components/icon-font';
import { parseMessage2Html } from '~/utils/jsx';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import {
    PLATFORM_SOCKET_EXPORT_ERROR_TYPE,
    SOCKET_EXPORT_ERROR_TYPE,
    SOCKET_EXPORT_TYPE,
    SOCKET_MESSAGE_TYPE
} from './constant';

const SOCKET_PATH = {
    MESSAGE: 'site_message',
    ERROR: 'error'
};

interface MessageData {
    type: string;
    instanceId: string;
}

interface NoticeData {
    type: string;
    instanceId?: string;
}

const AUTO_CLOSE_MESSAGE_DURATION = 4000;

class Socket {
    private CONFIG = {
        NOTIFICATION: {
            transports: ['websocket'],
            query: {
                type: 'notification',
                'x-csrf-token': window.jdy_csrf_token
            }
        },
        WIDGET: {
            transports: ['websocket'],
            query: {
                type: 'widget',
                'x-csrf-token': window.jdy_csrf_token
            }
        }
    };

    private notificationSocket: any;
    private isSiteMsgInit: boolean;
    private asyncTaskMap: any;
    private messageChannel: BroadcastChannel;

    constructor() {
        if (window.BroadcastChannel) {
            this.messageChannel = new BroadcastChannel('message-sync-channel');
            this.messageChannel.onmessage = (event) => {
                if (
                    event.data.type === SOCKET_MESSAGE_TYPE.CLOSE_MESSAGE &&
                    event.data.instanceId
                ) {
                    Message.remove(event.data.instanceId);
                }
            };
        }
    }

    initSocket(config: any) {
        const socket = io(
            HOST.WEBSOCKET_SITE,
            assign(
                {
                    reconnectionAttempts: 100, // 最多重连100次
                    reconnectionDelay: 20000, // 重连间隔20s
                    reconnectionDelayMax: 60000, // 最大重连间隔60s
                    path: getBasePathRouter('/socket')
                },
                config
            )
        );
        socket.on(SOCKET_PATH.ERROR, (err: any) => console.error(err));
        return socket;
    }

    onNotification(path: string, callback: (res: any) => void) {
        if (!this.notificationSocket) {
            this.notificationSocket = this.initSocket(this.CONFIG.NOTIFICATION);
        }
        this.notificationSocket.on(path, callback);
    }

    offNotification(path: string, callback: (res: any) => void) {
        if (this.notificationSocket) {
            this.notificationSocket.off(path, callback);
        }
    }

    setExcelExport(taskId: string, onSuccess?: (taskId: string) => void) {
        if (!taskId) {
            return;
        }
        this.asyncTaskMap = this.asyncTaskMap || {};
        this.asyncTaskMap['excelExport'] = this.asyncTaskMap['excelExport'] || {};
        if (!this.asyncTaskMap.excelExport[taskId]) {
            Message.add({
                key: taskId,
                messageType: 'notification',
                title: Intl.get('export.excel.notice.title'),
                content: Intl.getHTML('export.excel.notice.content'),
                duration: AUTO_CLOSE_MESSAGE_DURATION,
                icon: <IconFont type="async" style={{ color: COLOR_VAR.success }} />,
                onClose: () => {
                    this.closeMessage({
                        type: SOCKET_MESSAGE_TYPE.CLOSE_MESSAGE,
                        instanceId: taskId
                    });
                }
            });
            this.asyncTaskMap.excelExport[taskId] = {
                onSuccess
            };
        }
    }

    getExportNoticeCfg(notice: any) {
        const isErrorType =
            SOCKET_EXPORT_ERROR_TYPE.includes(notice.type) ||
            PLATFORM_SOCKET_EXPORT_ERROR_TYPE.includes(notice.type);
        if (isErrorType) {
            return {
                title: Intl.get('export.excel.fileFail'),
                duration: 0,
                icon: <IconFont style={{ color: COLOR_VAR.error }} type="close-circle" />
            };
        }
        return {
            title: Intl.get('export.excel.fileSuccess'),
            duration: 0,
            icon: <IconFont style={{ color: COLOR_VAR.success }} type="select" />
        };
    }

    getImportNoticeCfg(importNotice: {
        type: string;
        formName: string;
        imported?: number;
        failed: number;
        updated?: number;
        msgUrl?: string;
    }) {
        const { type, formName, imported, failed, updated, msgUrl } = importNotice;
        const noticeCfg = {
            title: Intl.get('excel.import.complete'),
            icon: <IconFont style={{ color: COLOR_VAR.success }} type="select" />
        };
        const hasFailed = failed > 0;
        const autoClose = hasFailed && failed < 30;

        if (msgUrl) {
            assign(noticeCfg, {
                duration: autoClose ? AUTO_CLOSE_MESSAGE_DURATION : 0,
                text4Ok: Intl.get('import.error.report'),
                onOk: () => {
                    openInNewTab(msgUrl);
                    this.closeMessage(importNotice);
                }
            });
        }
        switch (type) {
            case 'excel_import_complete':
                if (hasFailed) {
                    return assign(noticeCfg, {
                        content: Intl.getHTML('import.complete.insert.fail.notify', {
                            name: formName,
                            successCount: imported,
                            failCount: failed
                        })
                    });
                } else {
                    return assign(noticeCfg, {
                        content: Intl.getHTML('import.complete.insert.success.notify', {
                            name: formName,
                            count: imported
                        })
                    });
                }
            case 'excel_import_update_complete':
                if (hasFailed) {
                    return assign(noticeCfg, {
                        content: Intl.getHTML('import.complete.update.fail.notify', {
                            name: formName,
                            successCount: updated,
                            failCount: failed
                        })
                    });
                } else {
                    return assign(noticeCfg, {
                        content: Intl.getHTML('import.complete.update.success.notify', {
                            name: formName,
                            count: updated
                        })
                    });
                }
            case 'excel_import_upsert_complete':
                if (hasFailed) {
                    return assign(noticeCfg, {
                        content: Intl.getHTML('import.complete.upsert.fail.notify', {
                            name: formName,
                            updateCount: updated,
                            insertCount: imported,
                            failCount: failed
                        })
                    });
                } else {
                    return assign(noticeCfg, {
                        content: Intl.getHTML('import.complete.upsert.success.notify', {
                            name: formName,
                            updateCount: updated,
                            insertCount: imported
                        })
                    });
                }
        }
    }

    initSiteMsg() {
        if (this.isSiteMsgInit) {
            return;
        }
        this.isSiteMsgInit = true;
        this.asyncTaskMap = this.asyncTaskMap || {};
        this.onNotification(SOCKET_PATH.MESSAGE, (rawNotice) => {
            if (!rawNotice) {
                return;
            }
            let content: React.ReactNode = parseMessage2Html(rawNotice, EMPTY_FN, false);

            const instanceId = rawNotice.instance_id ?? getUUID();

            const notice = {
                ...rawNotice,
                instanceId
            };

            if (notice.link && notice.linkText) {
                // 【立即下载】与【下载错误报告】改为为button样式，其余保持原来自定义link
                if (
                    !includes(
                        ['file_export_complete', 'platform_file_export_complete', 'crm'],
                        notice.type
                    )
                ) {
                    content = (
                        <>
                            {content}
                            <div
                                style={{ textAlign: 'right' }}
                                onClick={() => this.closeMessage(notice)}
                            >
                                <Hyperlink
                                    compact
                                    underline={false}
                                    className="fx-message-color-primary"
                                    href={notice.link}
                                >
                                    {notice.linkText}
                                </Hyperlink>
                            </div>
                        </>
                    );
                }
            }

            let noticeConfig: {
                messageType: 'toast' | 'notification';
                key?: string;
                [key: string]: any;
            } | null = {
                messageType: 'notification',
                title: notice.subject,
                key: instanceId,
                duration: AUTO_CLOSE_MESSAGE_DURATION,
                content: content,
                onClose: () => {
                    this.closeMessage(notice);
                }
            };
            switch (notice.type) {
                case 'excel_import_complete':
                case 'excel_import_update_complete':
                case 'excel_import_upsert_complete':
                    assign(noticeConfig, this.getImportNoticeCfg(notice));
                    break;
                case 'file_export_complete':
                case 'platform_file_export_complete':
                    assign(
                        noticeConfig,
                        {
                            text4Ok: notice.linkText,
                            onOk: () => {
                                openInNewTab(notice.link);
                                post(getApi(API.message.set_as_read), { messages: notice._id });
                                this.closeMessage(notice);
                            }
                        },
                        this.getExportNoticeCfg(notice)
                    );
                    this.triggerExportComplete(notice);
                    break;
                case 'file_export_limit_failed':
                case 'file_export_data_limit':
                case 'file_export_failed':
                case 'file_export_memory_failed':
                case 'platform_file_export_failed':
                    assign(noticeConfig, this.getExportNoticeCfg(notice));
                    this.triggerExportComplete(notice);
                    break;
                case 'batch_update_member_begin':
                    assign(noticeConfig, {
                        title: Intl.get('fxp.base.bg.intra_org.modifying_member_info.tip'),
                        content: Intl.get('fxp.base.bg.intra_org.close_dialog_modify_in_bg.tip'),
                        icon: <IconFont type="async" style={{ color: COLOR_VAR.success }} />
                    });
                    break;
                case 'batch_update_member_done': {
                    const { success, fail } = notice;
                    if (fail > 0) {
                        assign(noticeConfig, {
                            title: Intl.get('fxp.base.bg.intra_org.batch_modify_partial.tip'),
                            content: Intl.getHTML(
                                'fxp.base.bg.intra_org.memebers_modified_partial.tip',
                                { success, fail }
                            ),
                            icon: (
                                <IconFont
                                    style={{ color: COLOR_VAR.warning }}
                                    type="warning-circle"
                                />
                            )
                        });
                    } else {
                        assign(noticeConfig, {
                            title: Intl.get('fxp.base.bg.intra_org.batch_modify_complete.tip'),
                            content: Intl.getHTML(
                                'fxp.base.bg.intra_org.memebers_batch_modified.tip',
                                {
                                    success
                                }
                            ),
                            icon: <IconFont style={{ color: COLOR_VAR.success }} type="select" />
                        });
                    }
                    this.triggerMemberBatchEditDone();
                    break;
                }
                case 'batch_update_member_error':
                    assign(noticeConfig, {
                        title: Intl.get('fxp.base.bg.intra_org.batch_modify_fail.tip'),
                        content: Intl.getHTML('fxp.base.bg.intra_org.memebers_modified_fail.tip', {
                            fail: notice.fail
                        }),
                        icon: <IconFont style={{ color: COLOR_VAR.error }} type="close-circle" />
                    });
                    break;
                default: {
                    noticeConfig = null;
                    break;
                }
            }
            if (noticeConfig) {
                Message.add(noticeConfig);
            }
        });
    }

    triggerExportComplete(notice: any) {
        const taskId = notice.params?.task_id;
        if (taskId) {
            const taskType = this.getTaskType(notice);
            const onSuccess = this.asyncTaskMap[taskType]?.[taskId]?.onSuccess;
            onSuccess?.(taskId);
        }
    }

    triggerMemberBatchEditDone() {
        this.asyncTaskMap?.memberBatchEdit?.callback?.();
    }

    onMemberBatchEditDone(callback: () => void) {
        this.asyncTaskMap = this.asyncTaskMap || {};
        this.asyncTaskMap.memberBatchEdit = this.asyncTaskMap.memberBatchEdit || {};
        this.asyncTaskMap.memberBatchEdit.callback = callback;
    }

    private getTaskType(notice: NoticeData) {
        let taskType = notice.type;
        if (SOCKET_EXPORT_TYPE.includes(taskType)) {
            taskType = 'excelExport';
        }

        return taskType;
    }

    /**
     * 关闭提醒框和同源页面的同实例消息提醒框
     * @param notice socket消息的内容
     */
    private closeMessage(notice: NoticeData) {
        const { instanceId } = notice;
        if (instanceId) {
            Message.remove(instanceId);
            this.broadcastMessage({
                type: SOCKET_MESSAGE_TYPE.CLOSE_MESSAGE,
                instanceId
            });
        }
    }

    private broadcastMessage(data: MessageData) {
        if (this.messageChannel) {
            this.messageChannel.postMessage(data);
        }
    }
}

const socket = new Socket();

export { socket, SOCKET_PATH };
